/**
 * Project members management panel.
 *
 * Shows members list, invite form, role management, and ownership transfer.
 * Used as a Dialog opened from the Project page toolbar.
 */

import { useState } from "react";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";

import type { ProjectMember } from "../../api/members";
import {
  useMembers,
  useInviteMember,
  useUpdateRole,
  useRemoveMember,
  useTransferOwnership,
} from "../../hooks/useMembers";
import { useCurrentRole } from "../../hooks/useCurrentRole";

const ROLE_COLORS: Record<string, "purple" | "blue" | "green" | "gray" | "amber"> = {
  owner: "purple",
  admin: "blue",
  editor: "green",
  viewer: "gray",
};

function RoleBadge({ role, pending }: { role: string; pending?: boolean }) {
  if (pending) return <Badge color="amber">Pending</Badge>;
  return <Badge color={ROLE_COLORS[role] ?? "gray"}>{role}</Badge>;
}

interface MembersPanelProps {
  projectId: string;
}

export function MembersPanel({ projectId }: MembersPanelProps) {
  const { data: members = [], isLoading } = useMembers(projectId);
  const { canManageMembers, role: currentRole } = useCurrentRole(projectId);
  const inviteMutation = useInviteMember(projectId);
  const updateRoleMutation = useUpdateRole(projectId);
  const removeMutation = useRemoveMember(projectId);
  const transferMutation = useTransferOwnership(projectId);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");

  const accepted = members.filter((m) => !m.pending);
  const pending = members.filter((m) => m.pending);

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate(
      { email: inviteEmail.trim(), role: inviteRole as "admin" | "editor" | "viewer" },
      { onSuccess: () => { setInviteEmail(""); } },
    );
  };

  return (
    <Box p="4" style={{ maxWidth: 640 }}>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Text size="5" weight="bold">Members</Text>
          <Text size="2" color="gray">You are {currentRole === "viewer" ? "a" : "an"} {currentRole}</Text>
        </Flex>

        {/* Invite form */}
        {canManageMembers && (
          <Flex gap="2" align="end">
            <Box style={{ flex: 1 }}>
              <TextField.Root
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              />
            </Box>
            <Select.Root value={inviteRole} onValueChange={setInviteRole}>
              <Select.Trigger style={{ minWidth: 100 }} />
              <Select.Content>
                <Select.Item value="editor">Editor</Select.Item>
                <Select.Item value="admin">Admin</Select.Item>
                <Select.Item value="viewer">Viewer</Select.Item>
              </Select.Content>
            </Select.Root>
            <Button
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              Invite
            </Button>
          </Flex>
        )}

        {inviteMutation.isError && (
          <Text size="2" color="red">{String(inviteMutation.error)}</Text>
        )}

        {/* Members table */}
        {isLoading ? (
          <Text size="2" color="gray">Loading members...</Text>
        ) : (
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                {canManageMembers && <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {accepted.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  canManage={canManageMembers}
                  onUpdateRole={(role) =>
                    updateRoleMutation.mutate({ memberId: m.id, role })
                  }
                  onRemove={() => removeMutation.mutate(m.id)}
                  onTransfer={() => transferMutation.mutate(m.user_id)}
                  isCurrentOwner={currentRole === "owner"}
                />
              ))}
              {pending.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  canManage={canManageMembers}
                  onRemove={() => removeMutation.mutate(m.id)}
                  pending
                />
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Flex>
    </Box>
  );
}

interface MemberRowProps {
  member: ProjectMember;
  canManage: boolean;
  onUpdateRole?: (role: string) => void;
  onRemove?: () => void;
  onTransfer?: () => void;
  isCurrentOwner?: boolean;
  pending?: boolean;
}

function MemberRow({
  member,
  canManage,
  onUpdateRole,
  onRemove,
  onTransfer,
  isCurrentOwner,
  pending,
}: MemberRowProps) {
  const isOwner = member.role === "owner";

  return (
    <Table.Row>
      <Table.Cell>
        <Flex align="center" gap="2">
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--accent-9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {(member.display_name || member.email || "?").charAt(0).toUpperCase()}
          </Box>
          <Text size="2">{member.display_name || member.email || member.user_id.slice(0, 8) + "…"}</Text>
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Text size="2" color="gray">{member.email ?? "—"}</Text>
      </Table.Cell>
      <Table.Cell>
        <RoleBadge role={member.role} pending={pending} />
      </Table.Cell>
      {canManage && (
        <Table.Cell>
          <Flex gap="2">
            {!isOwner && !pending && onUpdateRole && (
              <Select.Root
                value={member.role}
                onValueChange={onUpdateRole}
              >
                <Select.Trigger variant="ghost" style={{ minWidth: 80 }} />
                <Select.Content>
                  <Select.Item value="admin">Admin</Select.Item>
                  <Select.Item value="editor">Editor</Select.Item>
                  <Select.Item value="viewer">Viewer</Select.Item>
                </Select.Content>
              </Select.Root>
            )}
            {!isOwner && onRemove && (
              <AlertDialog.Root>
                <AlertDialog.Trigger>
                  <Button variant="ghost" color="red" size="1">
                    Remove
                  </Button>
                </AlertDialog.Trigger>
                <AlertDialog.Content>
                  <AlertDialog.Title>Remove member</AlertDialog.Title>
                  <AlertDialog.Description>
                    Remove {member.display_name || member.email} from this project?
                  </AlertDialog.Description>
                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray">Cancel</Button>
                    </AlertDialog.Cancel>
                    <AlertDialog.Action>
                      <Button color="red" onClick={onRemove}>Remove</Button>
                    </AlertDialog.Action>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>
            )}
            {isOwner && isCurrentOwner && onTransfer && (
              <AlertDialog.Root>
                <AlertDialog.Trigger>
                  <Button variant="ghost" size="1">Transfer</Button>
                </AlertDialog.Trigger>
                <AlertDialog.Content>
                  <AlertDialog.Title>Transfer ownership</AlertDialog.Title>
                  <AlertDialog.Description>
                    This will make {member.display_name || member.email} the new
                    owner and demote you to admin. This cannot be undone easily.
                  </AlertDialog.Description>
                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray">Cancel</Button>
                    </AlertDialog.Cancel>
                    <AlertDialog.Action>
                      <Button color="red" onClick={onTransfer}>
                        Transfer Ownership
                      </Button>
                    </AlertDialog.Action>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>
            )}
          </Flex>
        </Table.Cell>
      )}
    </Table.Row>
  );
}
